import { createContext, useContext, useEffect, useState } from "react";

// "system" é a escolha do usuário, não uma aparência: quem precisa saber se a
// tela está clara ou escura de fato deve ler `resolvedTheme`, nunca `theme`.
type Theme = "dark" | "light" | "system";
type ResolvedTheme = "dark" | "light";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultPrimaryColor?: string;
  defaultFontFamily?: string;
  storageKey?: string;
  primaryColorStorageKey?: string;
  fontFamilyStorageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  fontFamily: string;
  setFontFamily: (font: string) => void;
};

const initialState: ThemeProviderState = {
  theme: "light",
  resolvedTheme: "light",
  setTheme: () => null,
  primaryColor: "",
  setPrimaryColor: () => null,
  fontFamily: "Google Sans",
  setFontFamily: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

const prefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export function ThemeProvider({
  children,
  defaultTheme = "light",
  defaultPrimaryColor = "",
  defaultFontFamily = "Google Sans",
  storageKey = "vite-ui-theme",
  primaryColorStorageKey = "vite-ui-primary-color",
  fontFamilyStorageKey = "vite-ui-font-family",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    prefersDark() ? "dark" : "light"
  );

  const [primaryColor, setPrimaryColor] = useState<string>(
    () => localStorage.getItem(primaryColorStorageKey) || defaultPrimaryColor
  );

  const [fontFamily, setFontFamily] = useState<string>(
    () => localStorage.getItem(fontFamilyStorageKey) || defaultFontFamily
  );

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  // Só faz sentido acompanhar a preferência do SO enquanto ela é a escolha
  // ativa; com tema fixo o listener é ruído.
  useEffect(() => {
    if (theme !== "system") return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) =>
      setSystemTheme(event.matches ? "dark" : "light");

    setSystemTheme(media.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    const root = window.document.documentElement;
    if (primaryColor) {
      root.style.setProperty("--primary", primaryColor);
      root.style.setProperty("--ring", primaryColor);
      root.style.setProperty("--sidebar-primary", primaryColor);
      root.style.setProperty("--chart-1", primaryColor);
    } else {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--sidebar-primary");
      root.style.removeProperty("--chart-1");
    }
  }, [primaryColor]);

  useEffect(() => {
    const root = window.document.documentElement;
    const fontMapping: Record<string, string> = {
      "Google Sans": '"Google Sans", sans-serif',
      "Inter": '"Inter", sans-serif',
      "Plus Jakarta Sans": '"Plus Jakarta Sans", sans-serif',
      "Host Grotesk": '"Host Grotesk", sans-serif',
      "Space Grotesk": '"Space Grotesk", sans-serif',
      "Outfit": '"Outfit", sans-serif',
      "Sora": '"Sora", sans-serif',
      "Manrope": '"Manrope", sans-serif',
      "Montserrat": '"Montserrat", sans-serif',
      "Roboto": '"Roboto", sans-serif',
      "Sistema": 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };

    const fontValue = fontMapping[fontFamily] || '"Google Sans", sans-serif';
    root.style.setProperty("--font-sans", fontValue);
  }, [fontFamily]);

  const value = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    primaryColor,
    setPrimaryColor: (color: string) => {
      localStorage.setItem(primaryColorStorageKey, color);
      setPrimaryColor(color);
    },
    fontFamily,
    setFontFamily: (font: string) => {
      localStorage.setItem(fontFamilyStorageKey, font);
      setFontFamily(font);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
